import { useState, useRef, useCallback } from 'react';
import { toPng } from 'html-to-image';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ShareJourneyCard({ pieces, studiedPieces, discount, userName, onClose }) {
  const cardRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);

  const corners = pieces.filter(p => p.piece_type === 'corner');
  const borders = pieces.filter(p => p.piece_type === 'border');
  const completionDate = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const discountPct = discount?.discount_pct || 0;
  const displayName = userName || 'A Faithful Student';

  const handleDownload = useCallback(async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        quality: 0.95,
        pixelRatio: 2,
        backgroundColor: '#060503',
      });
      const link = document.createElement('a');
      link.download = 'kings-return-mastery.png';
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Download failed:', err);
    }
    setDownloading(false);
  }, []);

  const handleShare = useCallback(async () => {
    if (navigator.share) {
      try {
        if (cardRef.current) {
          const dataUrl = await toPng(cardRef.current, { quality: 0.9, pixelRatio: 2, backgroundColor: '#060503' });
          const blob = await (await fetch(dataUrl)).blob();
          const file = new File([blob], 'kings-return-mastery.png', { type: 'image/png' });
          await navigator.share({
            title: 'I Mastered The King\'s Return Puzzle!',
            text: `I've studied all 10 puzzle pieces of The King's Return and unlocked the complete picture of biblical eschatology.`,
            files: [file],
          });
        }
      } catch (err) {
        if (err.name !== 'AbortError') handleCopyLink();
      }
    } else {
      handleCopyLink();
    }
  }, []);

  const handleCopyLink = useCallback(() => {
    const text = `I mastered The King's Return Puzzle! All 10 pieces studied, the frame is complete. Check it out at ${API_URL}/puzzle`;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, []);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="share-journey-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />

      <div className="relative z-10 max-w-md w-full">
        {/* The Shareable Card */}
        <div ref={cardRef} className="rounded-2xl overflow-hidden" style={{ background: '#060503' }}>
          {/* Card Inner */}
          <div className="relative p-6" style={{
            background: 'linear-gradient(180deg, #0f0a04 0%, #080604 40%, #060503 100%)',
          }}>
            {/* Decorative border */}
            <div className="absolute inset-3 rounded-xl pointer-events-none" style={{
              border: '1px solid rgba(217,119,6,0.15)',
              boxShadow: 'inset 0 0 40px rgba(217,119,6,0.03)',
            }} />

            {/* Corner ornaments */}
            {['top-3 left-3', 'top-3 right-3', 'bottom-3 left-3', 'bottom-3 right-3'].map((pos, i) => (
              <div key={i} className={`absolute ${pos} w-4 h-4`} style={{
                borderTop: i < 2 ? '2px solid rgba(217,119,6,0.3)' : 'none',
                borderBottom: i >= 2 ? '2px solid rgba(217,119,6,0.3)' : 'none',
                borderLeft: i % 2 === 0 ? '2px solid rgba(217,119,6,0.3)' : 'none',
                borderRight: i % 2 === 1 ? '2px solid rgba(217,119,6,0.3)' : 'none',
              }} />
            ))}

            {/* Header */}
            <div className="text-center mb-5 pt-2">
              <p className="text-[8px] tracking-[0.4em] uppercase font-mono mb-2" style={{ color: 'rgba(217,119,6,0.4)' }}>
                Trump of the Lord Ministries
              </p>
              <h2 className="text-xl font-serif font-bold mb-1" style={{
                background: 'linear-gradient(180deg, #fcd34d 0%, #d97706 60%, #92400e 100%)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              }}>
                Puzzle Complete
              </h2>
              <div className="w-12 h-px mx-auto mt-2" style={{ background: 'linear-gradient(90deg, transparent, rgba(217,119,6,0.4), transparent)' }} />
            </div>

            {/* Puzzle Frame Visualization */}
            <div className="flex justify-center mb-5">
              <div className="grid grid-cols-4 gap-1" style={{ width: '160px' }}>
                {/* Row 1: C B B C */}
                {[corners[0], borders[0], borders[1], corners[1]].map((p, i) => (
                  <div key={`r1-${i}`} className="w-9 h-9 rounded flex items-center justify-center"
                    style={{
                      background: `radial-gradient(circle, ${p?.color || '#d97706'}20, ${p?.color || '#d97706'}08)`,
                      border: `1px solid ${p?.color || '#d97706'}40`,
                    }}>
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke={p?.color || '#d97706'} strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                ))}
                {/* Row 2: B _ _ B */}
                {[borders[5], null, null, borders[2]].map((p, i) => (
                  <div key={`r2-${i}`} className="w-9 h-9 rounded flex items-center justify-center"
                    style={p ? {
                      background: `radial-gradient(circle, ${p.color}20, ${p.color}08)`,
                      border: `1px solid ${p.color}40`,
                    } : { background: 'rgba(217,119,6,0.03)' }}>
                    {p && <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke={p.color} strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>}
                  </div>
                ))}
                {/* Row 3: C B B C */}
                {[corners[2], borders[3], borders[4], corners[3]].map((p, i) => (
                  <div key={`r3-${i}`} className="w-9 h-9 rounded flex items-center justify-center"
                    style={{
                      background: `radial-gradient(circle, ${p?.color || '#d97706'}20, ${p?.color || '#d97706'}08)`,
                      border: `1px solid ${p?.color || '#d97706'}40`,
                    }}>
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke={p?.color || '#d97706'} strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                ))}
              </div>
            </div>

            {/* Scripture References */}
            <div className="mb-5">
              <p className="text-[7px] tracking-[0.3em] uppercase font-mono text-center mb-2" style={{ color: 'rgba(217,119,6,0.3)' }}>
                The Ten Treasures
              </p>
              <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 px-4">
                {pieces.map((p) => (
                  <span key={p.piece_id} className="text-[9px] font-serif" style={{ color: `${p.color}90` }}>
                    {p.reference}
                  </span>
                ))}
              </div>
            </div>

            {/* User Info */}
            <div className="text-center mb-4">
              <p className="text-sm font-serif font-semibold" style={{ color: '#fcd34d' }}>
                {displayName}
              </p>
              <p className="text-[10px] font-serif mt-1" style={{ color: 'rgba(217,119,6,0.5)' }}>
                Completed {completionDate}
              </p>
            </div>

            {/* Stats */}
            <div className="flex items-center justify-center gap-6 mb-4">
              <div className="text-center">
                <p className="text-lg font-bold font-mono" style={{ color: '#fbbf24' }}>10/10</p>
                <p className="text-[8px] font-mono uppercase tracking-wider" style={{ color: 'rgba(217,119,6,0.4)' }}>Pieces</p>
              </div>
              <div className="w-px h-8" style={{ background: 'rgba(217,119,6,0.15)' }} />
              <div className="text-center">
                <p className="text-lg font-bold font-mono" style={{ color: '#34d399' }}>{discountPct}%</p>
                <p className="text-[8px] font-mono uppercase tracking-wider" style={{ color: 'rgba(16,185,129,0.4)' }}>Earned</p>
              </div>
              <div className="w-px h-8" style={{ background: 'rgba(217,119,6,0.15)' }} />
              <div className="text-center">
                <p className="text-lg font-bold font-mono" style={{ color: '#c084fc' }}>3</p>
                <p className="text-[8px] font-mono uppercase tracking-wider" style={{ color: 'rgba(192,132,252,0.4)' }}>Rewards</p>
              </div>
            </div>

            {/* Quote */}
            <div className="text-center pt-2 pb-1">
              <p className="text-[9px] font-serif italic leading-relaxed" style={{ color: 'rgba(217,119,6,0.35)' }}>
                "The experienced puzzle builder knows exactly what to do."
              </p>
              <p className="text-[7px] font-mono mt-2 tracking-[0.2em]" style={{ color: 'rgba(217,119,6,0.2)' }}>
                THE KING'S RETURN
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-center gap-3 mt-4">
          <button
            onClick={handleDownload}
            disabled={downloading}
            data-testid="btn-download-card"
            className="flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-serif font-semibold transition-all duration-300 hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, #b45309, #d97706)',
              color: '#fff',
              boxShadow: '0 4px 15px rgba(217,119,6,0.3)',
            }}
          >
            {downloading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            )}
            {downloading ? 'Saving...' : 'Download Image'}
          </button>

          <button
            onClick={handleShare}
            data-testid="btn-share-card"
            className="flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-serif font-semibold transition-all duration-300 hover:scale-105 border"
            style={{
              background: 'rgba(15,10,5,0.8)',
              borderColor: 'rgba(217,119,6,0.3)',
              color: '#fbbf24',
            }}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
            {copied ? 'Copied!' : 'Share'}
          </button>

          <button
            onClick={onClose}
            data-testid="btn-close-share"
            className="flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300 hover:scale-105"
            style={{ background: 'rgba(15,10,5,0.8)', border: '1px solid rgba(120,80,20,0.2)' }}
          >
            <svg className="w-4 h-4 text-amber-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
